import { toast } from 'react-hot-toast';
import { getToken, clearToken } from './tokenStore';

/**
 * API client for Sanctum bearer-token authentication.
 *
 * Pulls the token from tokenStore on every request and injects it as an
 * Authorization: Bearer header. No CSRF cookie, no `credentials: 'include'`.
 * Works across primary, subdomain, and fully custom whitelabel domains.
 */
class SimpleApiClient {
  /**
   * Get the correct API base URL based on current hostname.
   * Returns '' (relative) so requests go to the same origin via nginx/Vite.
   */
  private getApiBaseUrl(): string {
    return '';
  }

  async request(url: string, options: RequestInit = {}): Promise<Response> {
    const defaultHeaders: Record<string, string> = {
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    };

    const isFormData = options.body instanceof FormData;
    if (!isFormData) {
      defaultHeaders['Content-Type'] = 'application/json';
    }

    const token = getToken();
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }

    const baseUrl = this.getApiBaseUrl();
    const fullUrl = baseUrl + url;

    const response = await fetch(fullUrl, {
      ...options,
      headers: {
        ...defaultHeaders,
        ...((options.headers as Record<string, string>) || {}),
      },
    });

    return response;
  }

  async get(url: string, options: Omit<RequestInit, 'method'> = {}): Promise<any> {
    const response = await this.request(url, { ...options, method: 'GET' });
    return this.handleResponse(response);
  }

  async post(url: string, body?: any, options: Omit<RequestInit, 'method' | 'body'> = {}): Promise<any> {
    const response = await this.request(url, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
    return this.handleResponse(response);
  }

  async put(url: string, body?: any, options: Omit<RequestInit, 'method' | 'body'> = {}): Promise<any> {
    const response = await this.request(url, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
    return this.handleResponse(response);
  }

  async delete(url: string, options: Omit<RequestInit, 'method'> = {}): Promise<any> {
    const response = await this.request(url, { ...options, method: 'DELETE' });
    return this.handleResponse(response);
  }

  /**
   * Create EventSource for SSE. Browsers' EventSource doesn't allow custom
   * headers, so SSE endpoints must accept the token via query string OR
   * the SSE controller must accept the token from the access_token query
   * param (handled server-side via Sanctum's `actingAs`).
   */
  createEventSource(url: string): EventSource {
    const token = getToken();
    const separator = url.includes('?') ? '&' : '?';
    const fullUrl = token ? `${url}${separator}access_token=${encodeURIComponent(token)}` : url;

    return new EventSource(fullUrl);
  }

  async handleResponse<T>(response: Response, context?: string): Promise<T> {
    if (!response.ok) {
      let errorMessage = `Request failed (${response.status})`;

      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        // Non-JSON response — keep default message.
      }

      if (response.status === 401) {
        // Token rejected — purge it so the next page load re-routes to sign-in.
        clearToken();
        toast.error('Please sign in to continue');
      } else if (context) {
        toast.error(`${context}: ${errorMessage}`);
      }

      throw new Error(errorMessage);
    }

    return response.json();
  }
}

export const apiClient = new SimpleApiClient();
export default SimpleApiClient;
